import { useCallback, useMemo } from 'react';

import { useDebouncedHandler } from '@shared/hooks/debounce';
import { type Language } from '@shared/LocaleContext';

export const getBrokerLanguage = (
  broker: { language?: string | null } | null | undefined,
  preferredLanguage?: Language | null,
): Language => {
  return preferredLanguage ?? (broker?.language as Language) ?? 'en';
};

export const useLanguageTranslations = <T extends Record<string, unknown>>(
  data: T | null | undefined,
  prefix: string,
  fallback?: { fallbackValue: string | null; fallbackLanguage: Language },
): Record<Language, string | null> => {
  /* eslint-disable react-hooks/exhaustive-deps */
  return useMemo(() => {
    const translations: Record<Language, string | null> = {
      en: (data?.[`${prefix}_en`] as string) ?? null,
      fr: (data?.[`${prefix}_fr`] as string) ?? null,
      de: (data?.[`${prefix}_de`] as string) ?? null,
      it: (data?.[`${prefix}_it`] as string) ?? null,
      es: (data?.[`${prefix}_es`] as string) ?? null,
      pl: (data?.[`${prefix}_pl`] as string) ?? null,
      pt: (data?.[`${prefix}_pt`] as string) ?? null,
    };

    if (
      fallback &&
      translations[fallback.fallbackLanguage] == null &&
      fallback.fallbackValue != null
    ) {
      translations[fallback.fallbackLanguage] = fallback.fallbackValue;
    }

    return translations;
  }, [
    data?.[`${prefix}_en`],
    data?.[`${prefix}_fr`],
    data?.[`${prefix}_de`],
    data?.[`${prefix}_it`],
    data?.[`${prefix}_es`],
    data?.[`${prefix}_pl`],
    data?.[`${prefix}_pt`],
    fallback?.fallbackValue,
    fallback?.fallbackLanguage,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */
};

export const useTranslationUpdate = (
  updateFn: (input: Record<string, unknown>) => void,
  prefix: string,
) => {
  const updateTranslation = useCallback(
    (language: Language, text: string) =>
      updateFn({ [`${prefix}_${language}`]: text }),
    [updateFn, prefix],
  );

  const debouncedTranslationUpdate = useDebouncedHandler(
    300,
    updateTranslation,
  );

  return { updateTranslation, debouncedTranslationUpdate };
};
